<template>
    <v-app>
        <v-content>
            <spinner />
            <v-container
                fill-height
            >
                <v-layout
                    align-center
                    justify-center
                >
                    <v-flex md5>
                        <h2 class="text-xs-center text-primary">
                            <img src="/img/appmixer-logo.svg">
                            <br>Backoffice
                            <br><span class="subheading">Sign-in</span>
                        </h2>

                        <v-form
                            v-if="!loadingConfig"
                            ref="form"
                            v-model="valid"
                            :lazy-validation="false"
                            @submit.prevent="doAuthenticate"
                            class="mt-5"
                        >
                            <div class="sso-login">
                                <v-btn
                                    v-if="ssoEnabled"
                                    color="error"
                                    class="mr-4 sso-login-button"
                                    @click="doAuthenticateSSO"
                                >
                                    Sign in with SSO
                                </v-btn>

                                <div
                                    v-if="ssoEnabled && (ssoEnforced || !showStandardLogin)"
                                    class="text-danger sso-login-error"
                                >
                                    {{ authError }}
                                </div>

                                <p
                                    v-if="ssoEnabled && !ssoEnforced"
                                    class="show-email-login-line"
                                >
                                    <v-switch
                                        v-model="showStandardLogin"
                                        color="red"
                                    >
                                        <template v-slot:prepend>
                                            <v-label>Show email login</v-label>
                                        </template>
                                    </v-switch>
                                </p>
                            </div>

                            <div
                                v-if="!ssoEnabled || (!ssoEnforced && showStandardLogin)"
                                class="standard-authenticate"
                            >
                                <v-text-field
                                    v-model="username"
                                    :rules="usernameRules"
                                    :disabled="isSendingForm"
                                    type="email"
                                    label="Username / Email"
                                    required
                                    @input="shouldShowPwd"
                                >
                                    <template #append>
                                        <v-fade-transition leave-absolute>
                                            <v-progress-circular
                                                v-if="isSendingForm"
                                                size="20"
                                                width="2"
                                                indeterminate
                                            />
                                        </v-fade-transition>
                                    </template>
                                </v-text-field>

                                <v-text-field
                                    v-model="password"
                                    :rules="passwordRules"
                                    type="password"
                                    label="Password"
                                    required
                                />

                                <div class="text-danger">
                                    {{ authError }}
                                </div>

                                <v-btn
                                    :disabled="!valid"
                                    color="primary"
                                    class="mr-4"
                                    @click="doAuthenticate"
                                >
                                    Sign-in
                                </v-btn>
                            </div>
                        </v-form>
                    </v-flex>
                </v-layout>
            </v-container>
        </v-content>
    </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Spinner from '../components/common/Spinner';
import UserRequests from '@/utils/requests/user';

export default {
    name: 'Login',
    metaInfo() {
        return {
            title: 'Appmixer Backoffice - Sign in'
        };
    },
    components: { Spinner },
    data() {
        return {
            isSendingForm: false,
            valid: true,
            loadingConfig: true,
            showStandardLogin: false,
            ssoEnforced: false,
            ssoEnabled: false,
            ssoDomains: [],
            overrideShowPwd: false,
            username: '',
            usernameRules: [
                v => !!v || 'Username is required'
            ],
            password: '',
            passwordRules: [
                v => !!v || 'Password is required'
            ]
        };
    },
    computed: {
        ...mapGetters('user', {
            accessToken: 'accessToken',
            refreshToken: 'refreshToken',
            authError: 'error'
        })
    },
    async beforeCreate() {
        if (this.accessToken) {
            this.$router.replace('/dashboard/modules');
        }
    },
    async created() {
        const ssoConfig = await UserRequests.getSSOConfig();

        this.ssoEnforced = ssoConfig.ssoEnforced;
        if (Array.isArray(ssoConfig.domains) && ssoConfig.domains.length > 0) {
            this.ssoEnabled = true;
            this.ssoDomains = ssoConfig.domains;
        }

        if (this.hasSSOParams()) {
            this.username = '';
            this.ssoEnabled = true;
            this.loadingConfig = false;
            this.isSendingForm = true;
            this.overrideShowPwd = false;
            await this.ssoAuthenticate();
        }

        this.loadingConfig = false;
    },
    methods: {
        ...mapActions('user', {
            authenticateUser: 'authenticate',
            authenticateUserWithSSO: 'authenticateWithSSO'
        }),
        hasSSOParams() {
            return !!this.$route.query['withSSO'] && !!this.$route.query['code'];
        },
        shouldShowPwd(email) {
            if (!this.ssoEnabled) {
                return;
            }
            const chunks = email.split('@');
            if (chunks.length < 2) {
                return;
            }
            const domain = chunks[chunks.length - 1].trim().toLowerCase();
            this.overrideShowPwd = this.ssoDomains.filter(d => d.trim().toLowerCase().startsWith(domain)).length === 0;
        },
        async doAuthenticate() {
            if (this.isSendingForm) {
                return;
            }
            this.isSendingForm = true;
            try {
                await this.authenticate();
            } finally {
                this.isSendingForm = false;
            }
        },
        async doAuthenticateSSO() {
            if (this.isSendingForm) {
                return;
            }
            this.isSendingForm = true;
            try {
                await this.ssoRedirect();
            } finally {
                this.isSendingForm = false;
            }
        },
        async authenticate() {
            if (!this.$refs.form.validate()) {
                return;
            }
            await this.authenticateUser({ username: this.username, password: this.password });
            if (this.accessToken) {
                this.$router.replace('/dashboard/modules');
            }
        },
        async ssoAuthenticate() {
            const redirectUri = this.getRedirectUri(this.username);
            await this.authenticateUserWithSSO({ code: this.$route.query['code'], redirectUri: redirectUri });
            if (this.accessToken) {
                this.$router.replace('/dashboard/modules');
            } else {
                this.$router.replace('/login');
                this.isSendingForm = false;
            }
        },
        async ssoRedirect() {
            const redirectUriParams = new URLSearchParams();
            redirectUriParams.append('withSSO', 'true');
            const redirectUri = this.getRedirectUri(this.username);
            const ssoRedirect = await UserRequests.getSSOAuthUrl(redirectUri);
            if (ssoRedirect && ssoRedirect.redirect) {
                window.location.href = ssoRedirect.redirect;
            }
        },
        getRedirectUri() {
            const redirectUriParams = new URLSearchParams();
            redirectUriParams.append('withSSO', 'true');
            return `${window.location.origin}/login?${redirectUriParams.toString()}`;
        }
    }
};
</script>

<style scoped>

</style>
